import { css } from '@emotion/react'
import { spacing } from '@instacart/ids-core'
import { useIntl } from 'common'
import { InputText, Select, CardTitle, CardCollapse } from 'components/ids-ads'
import { Label } from 'components/ids-ads/molecules'
import { InfoIcon } from 'components/molecules/InputFields/InfoIcon'
import { GenericMessageDescriptor } from 'locales/types'
import { useBillPayerOptions } from 'service/billing'

type LabelOption = {
  value: string
  label: string | number
}

const PURCHASE_ORDER_MAX_LENGTH = 50

const useStyles = () => {
  return {
    root: css({
      display: 'grid',
      columnGap: spacing.s24,
      rowGap: spacing.s16,
      gridTemplateColumns: '1fr 1fr',
    }),
    field: css({
      marginTop: spacing.s4,
    }),
    tooltipLabel: css({
      display: 'flex',
      gap: spacing.s4,
      alignItems: 'center',
    }),
  }
}

export type BillingCardProps = {
  title?: GenericMessageDescriptor
  handleChange(params: { id: string; value?: LabelOption['value'] }): void
  billPayer: string
  paymentType: string
  memo: string
  purchaseOrder: string
  paymentTypeOptions?: LabelOption[]
  billPayerOptions?: LabelOption[]
  expandOnMount?: boolean
}

const valueToLabel = (options: LabelOption[], value?: string): LabelOption | undefined =>
  options.find(opt => opt.value === value)

export default function BillingCard({
  handleChange,
  title = 'common.billing',
  memo,
  billPayer,
  paymentType,
  paymentTypeOptions,
  billPayerOptions,
  purchaseOrder,
  expandOnMount = false,
}: BillingCardProps) {
  const { genericFormatMessage: format } = useIntl()
  const defaultBillPayerOptions = useBillPayerOptions() as LabelOption[]
  const defaultPaymentTypeOptions: LabelOption[] = [
    { value: 'purchase_order', label: format('common.billing.purchase_order') },
    { value: 'account', label: format('common.account') },
  ]

  const billPlayerOpts = billPayerOptions ?? defaultBillPayerOptions
  const paymentTypeOpts = paymentTypeOptions ?? defaultPaymentTypeOptions

  const styles = useStyles()

  return (
    <CardCollapse
      expandOnMount={expandOnMount}
      data-testid="billing-card"
      trigger={
        <CardTitle css={{ marginBottom: 0 }} note={format('common.optional')}>
          {format(title)}
        </CardTitle>
      }
    >
      <div css={styles.root} id="billing-card">
        <div>
          <Label htmlFor="memo">{format('common.attention')}</Label>
          <InputText
            id="memo"
            name="memo"
            value={memo}
            css={styles.field}
            data-testid="memo-field"
            placeholder={format('common.billing.memo')}
            onChange={e => handleChange({ id: 'memo', value: e.target.value })}
          />
        </div>
        <div data-testid="billPayer-field">
          <Label htmlFor="billPayer">{format('common.billPayer')}</Label>
          <Select<LabelOption>
            block
            id="billPayer"
            variant="tertiary"
            css={styles.field}
            menuPlacement="auto"
            value={valueToLabel(billPlayerOpts, billPayer)}
            placeholder={format('common.billPayer')}
            options={billPayerOptions ?? defaultBillPayerOptions}
            onChange={e => handleChange({ id: 'billPayer', value: e?.value })}
          />
        </div>
        <div data-testid="paymentType-field">
          <Label htmlFor="paymentType">{format('common.paymentType')}</Label>
          <Select<LabelOption>
            block
            id="paymentType"
            css={styles.field}
            variant="tertiary"
            menuPlacement="auto"
            value={valueToLabel(paymentTypeOpts, paymentType)}
            placeholder={format('common.paymentType')}
            options={paymentTypeOptions ?? defaultPaymentTypeOptions}
            onChange={e => handleChange({ id: 'paymentType', value: e?.value })}
          />
        </div>
        <div>
          <span css={styles.tooltipLabel}>
            <Label htmlFor="purchaseOrder">{format('common.billing.purchase_order')}</Label>
            <InfoIcon
              info={format({
                id: 'common.billing.mandatoryPurchaseOrderToolTip',
              })}
              infoDescription={format({
                id: 'common.billing.mandatoryPurchaseOrderToolTip',
              })}
              compact
            />
          </span>
          <InputText
            css={styles.field}
            id="purchaseOrder"
            name="purchaseOrder"
            value={purchaseOrder}
            data-testid="purchaseOrder-field"
            placeholder={format('common.billing.purchase_order')}
            maxLength={PURCHASE_ORDER_MAX_LENGTH}
            onChange={e => handleChange({ id: 'purchaseOrder', value: e.target.value })}
          />
        </div>
      </div>
    </CardCollapse>
  )
}
